/**
 * Authentication Service
 * Core authentication functionality for Firesite SSO
 */
import { initializeApp, getApps } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendEmailVerification,
  updatePassword,
  updateProfile,
  onAuthStateChanged,
  getIdToken,
  getIdTokenResult,
} from 'firebase/auth';

import { firebaseConfig, authSettings } from '../core/config/firebase.js';
import { GlobalEvents, Events } from '../core/events/index.js';
import { apiService } from './api-service.js';

class AuthService {
  constructor() {
    this.app = null;
    this.auth = null;
    this.currentUser = null;
    this.tokenRefreshTimer = null;
    this.initialized = false;
    this.authStateUnsubscribe = null;
    this._refreshInProgress = false;
  }

  /**
   * Initialize the authentication service
   */
  async init() {
    if (this.initialized) return;

    try {
      // Initialize Firebase (use existing app if available)
      const existingApps = getApps();
      this.app = existingApps.length > 0 ? existingApps[0] : initializeApp(firebaseConfig);
      this.auth = getAuth(this.app);

      // Set up auth state listener
      this._setupAuthStateListener();

      // Set up redirect handler
      this._setupRedirectHandler();

      // Set persistence
      await this._setPersistence();

      this.initialized = true;
      GlobalEvents.emit(Events.SERVICE_READY, { service: 'AuthService' });
    } catch (error) {
      console.error('Failed to initialize AuthService:', error);
      GlobalEvents.emit(Events.SERVICE_ERROR, {
        service: 'AuthService',
        error: error.message,
      });
      throw error;
    }
  }

  /**
   * Set up Firebase auth persistence
   */
  async _setPersistence() {
    // Firebase v9+ handles persistence differently
    // Default is 'local' persistence which is what we want
    // No explicit setting needed unless changing from default
  }

  /**
   * Set up authentication state listener
   */
  _setupAuthStateListener() {
    this.authStateUnsubscribe = onAuthStateChanged(this.auth, async user => {
      console.log('🔔 FIREBASE onAuthStateChanged fired:', {
        user: user ? `${user.email} (${user.uid})` : 'NULL - NO USER',
        previousUser: this.currentUser ? this.currentUser.email : 'none',
        timestamp: new Date().toISOString()
      });
      
      const previousUser = this.currentUser;
      this.currentUser = user;

      if (user) {
        // User is signed in
        console.log('✅ Firebase user IS authenticated');
        await this._handleUserSignIn(user);
      } else {
        // User is signed out
        console.log('❌ Firebase user is NULL - NOT authenticated');
        this._handleUserSignOut();
      }

      // Emit auth state change event
      GlobalEvents.emit(Events.AUTH_STATE_CHANGED, {
        user,
        previousUser,
        isAuthenticated: !!user,
      });
    });
  }

  /**
   * Set up redirect handler for already authenticated users
   */
  _setupRedirectHandler() {
    GlobalEvents.on(Events.AUTH_REDIRECT_REQUIRED, async (data) => {
      try {
        console.log('🔄 Handling redirect for authenticated user:', data);
        await this._handleRedirectAuthentication(data.user, { token: null });
      } catch (error) {
        console.error('❌ Failed to handle redirect for authenticated user:', error);
        GlobalEvents.emit(Events.AUTH_ERROR, {
          error: 'Redirect failed for authenticated user',
          details: error.message
        });
      }
    });
  }

  /**
   * Handle user sign in
   */
  async _handleUserSignIn(user) {
    try {
      // Set up token refresh
      this._setupTokenRefresh();

      // Get custom claims
      const tokenResult = await getIdTokenResult(user);

      // Check if this is a redirect scenario (returnUrl exists)
      const returnUrl = sessionStorage.getItem('auth_return_url');
      
      if (returnUrl) {
        console.log('🔄 Redirect authentication detected, generating enhanced JWT...');
        // This is a redirect authentication - need to generate enhanced JWT and redirect
        await this._handleRedirectAuthentication(user, tokenResult);
      } else {
        console.log('🔍 Direct SSO authentication - no redirect needed');
        // Direct SSO authentication - emit normal auth events
        GlobalEvents.emit(Events.AUTH_LOGIN, {
          user: this._sanitizeUser(user),
          claims: tokenResult.claims,
          token: tokenResult.token,
        });
      }
    } catch (error) {
      console.error('Error handling sign in:', error);
      GlobalEvents.emit(Events.AUTH_ERROR, { error: error.message });
    }
  }

  /**
   * Handle redirect authentication scenario
   * Redirects back to calling service with Firebase token (no enhancement needed)
   * @param {Object} user - Firebase user object
   * @param {Object} tokenResult - Firebase ID token result
   * @private
   */
  async _handleRedirectAuthentication(user, tokenResult) {
    try {
      // Use Firebase token directly (backend validates Firebase tokens now)
      const authData = {
        token: tokenResult.token,
        user: this._sanitizeUser(user),
        claims: tokenResult.claims
      };
      
      // Perform redirect with authentication data
      await this._redirectWithAuthData(authData);
      
    } catch (error) {
      console.error('❌ Redirect authentication failed:', error);
      
      // Emit error event for UI handling
      GlobalEvents.emit(Events.AUTH_ERROR, {
        error: 'Authentication successful, but redirect failed. Please return to your application manually.',
        details: error.message
      });
      
      // Fall back to normal auth handling if redirect fails
      GlobalEvents.emit(Events.AUTH_LOGIN, {
        user: this._sanitizeUser(user),
        claims: tokenResult.claims,
        token: tokenResult.token,
      });
    }
  }

  /**
   * Generate enhanced JWT using existing backend API
   * @param {Object} firebaseUser - Firebase user object
   * @returns {Promise<Object>} Enhanced authentication data
   * @private
   */
  async _generateEnhancedJWT(firebaseUser) {
    try {
      const idToken = await getIdToken(firebaseUser, true);
      
      // Get the Firebase Functions endpoint
      const functionsUrl = await this._getFunctionsEndpoint();
      const enhanceTokenUrl = `${functionsUrl}/auth/token/enhance`;
      
      console.log('📡 Calling enhanced JWT endpoint:', enhanceTokenUrl);
      
      // Call the new enhanced JWT endpoint
      const response = await fetch(enhanceTokenUrl, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify({ 
          idToken,
          context: {
            source: 'sso_redirect',
            returnUrl: sessionStorage.getItem('auth_return_url'),
            serviceName: sessionStorage.getItem('auth_service_name'),
            state: sessionStorage.getItem('auth_state')
          }
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Enhanced JWT endpoint error:', response.status, errorText);
        throw new Error(`Enhanced JWT generation failed: ${response.status} ${response.statusText}`);
      }
      
      const authData = await response.json();
      console.log('✅ Enhanced JWT generated successfully');
      
      return authData;
      
    } catch (error) {
      console.error('❌ Enhanced JWT generation failed:', error);
      throw new Error(`Failed to generate enhanced JWT: ${error.message}`);
    }
  }

  /**
   * Get Firebase Functions endpoint URL
   * @returns {Promise<string>} Functions endpoint URL
   * @private
   */
  async _getFunctionsEndpoint() {
    // In development, use the local emulator
    // MCP Max functions run on port 5001 (not 5000)
    if (window.location.hostname === 'localhost') {
      return 'http://localhost:5001/firesite-ai-f3bc8/us-central1/api';
    }
    
    // In production, use the deployed functions
    return 'https://us-central1-firesite-ai-f3bc8.cloudfunctions.net/api';
  }

  /**
   * Redirect back to calling service with authentication data
   * @param {Object} authData - Enhanced authentication data
   * @private
   */
  async _redirectWithAuthData(authData) {
    try {
      const returnUrl = sessionStorage.getItem('auth_return_url');
      const serviceName = sessionStorage.getItem('auth_service_name');
      
      if (!returnUrl) {
        throw new Error('No return URL found in session storage');
      }
      
      console.log('🔄 Redirecting to service:', { returnUrl, serviceName });
      
      // Build redirect URL with ONLY the token (no PII in URL)
      const redirectUrl = new URL(returnUrl);
      redirectUrl.searchParams.set('token', authData.token);
      redirectUrl.searchParams.set('auth', 'success');
      
      // Include state parameter if it was provided (for security)
      const state = sessionStorage.getItem('auth_state');
      if (state) {
        redirectUrl.searchParams.set('state', state);
      }
      
      // DO NOT include user data in URL - token contains all necessary info
      
      // Clear stored auth parameters
      sessionStorage.removeItem('auth_return_url');
      sessionStorage.removeItem('auth_service_name');
      sessionStorage.removeItem('auth_state');
      
      console.log('✅ Redirecting to:', redirectUrl.toString());
      
      // Perform the redirect
      window.location.href = redirectUrl.toString();
      
    } catch (error) {
      console.error('❌ Redirect failed:', error);
      throw new Error(`Redirect failed: ${error.message}`);
    }
  }

  /**
   * Handle user sign out
   */
  _handleUserSignOut() {
    // Clear token refresh
    this._clearTokenRefresh();

    GlobalEvents.emit(Events.AUTH_LOGOUT, {});
  }

  /**
   * Set up automatic token refresh
   */
  _setupTokenRefresh() {
    this._clearTokenRefresh();

    this.tokenRefreshTimer = setInterval(async () => {
      // Prevent concurrent refresh operations
      if (this.currentUser && !this._refreshInProgress) {
        this._refreshInProgress = true;
        try {
          const token = await getIdToken(this.currentUser, true);
          GlobalEvents.emit(Events.AUTH_TOKEN_REFRESHED, { token });
        } catch (error) {
          console.error('Token refresh failed:', error);
          GlobalEvents.emit(Events.AUTH_ERROR, {
            error: 'Token refresh failed',
            details: error,
          });
        } finally {
          this._refreshInProgress = false;
        }
      }
    }, authSettings.tokenRefreshInterval);
  }

  /**
   * Clear token refresh timer
   */
  _clearTokenRefresh() {
    if (this.tokenRefreshTimer) {
      clearInterval(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }
  }

  /**
   * Sign in with email and password
   */
  async signIn(email, password) {
    try {
      GlobalEvents.emit(Events.SERVICE_LOADING, { service: 'AuthService', action: 'signIn' });

      const userCredential = await signInWithEmailAndPassword(this.auth, email, password);
      return {
        success: true,
        user: this._sanitizeUser(userCredential.user),
      };
    } catch (error) {
      GlobalEvents.emit(Events.AUTH_ERROR, { error: error.message });
      return {
        success: false,
        error: this._getAuthErrorMessage(error),
      };
    }
  }

  /**
   * Register a new user
   */
  async register(email, password, displayName = null) {
    try {
      GlobalEvents.emit(Events.SERVICE_LOADING, { service: 'AuthService', action: 'register' });

      const userCredential = await createUserWithEmailAndPassword(this.auth, email, password);

      // Update display name if provided
      if (displayName) {
        await updateProfile(userCredential.user, { displayName });
      }

      // Send verification email if required
      if (authSettings.emailVerificationRequired) {
        await this.sendVerificationEmail();
      }

      GlobalEvents.emit(Events.USER_CREATED, {
        user: this._sanitizeUser(userCredential.user),
      });

      return {
        success: true,
        user: this._sanitizeUser(userCredential.user),
      };
    } catch (error) {
      GlobalEvents.emit(Events.AUTH_ERROR, { error: error.message });
      return {
        success: false,
        error: this._getAuthErrorMessage(error),
      };
    }
  }

  /**
   * Sign out the current user
   */
  async signOut() {
    try {
      // First, revoke tokens on the backend if user is authenticated
      if (this.auth.currentUser) {
        try {
          const idToken = await getIdToken(this.auth.currentUser);
          const functionsUrl = await this._getFunctionsEndpoint();
          const logoutUrl = `${functionsUrl}/auth/logout`;
          
          console.log('📡 Calling backend logout to revoke tokens:', logoutUrl);
          
          await fetch(logoutUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`
            }
          });
          
          console.log('✅ Backend logout successful - tokens revoked');
        } catch (backendError) {
          console.warn('⚠️ Backend logout failed, proceeding with client logout:', backendError.message);
          // Continue with client logout even if backend fails
        }
      }
      
      // Then perform client-side logout
      await signOut(this.auth);
      console.log('✅ Client-side logout completed');
      
      return { success: true };
    } catch (error) {
      console.error('❌ Sign out error:', error);
      GlobalEvents.emit(Events.AUTH_ERROR, { error: error.message });
      return {
        success: false,
        error: this._getAuthErrorMessage(error),
      };
    }
  }

  /**
   * Send password reset email via custom API (uses SendGrid)
   */
  async sendPasswordResetEmail(email) {
    try {
      // Use custom API endpoint that sends branded SendGrid emails
      const response = await apiService.sendPasswordResetEmail(email);

      if (response.success) {
        return { success: true };
      } else {
        throw new Error(response.error || 'Failed to send reset email');
      }
    } catch (error) {
      GlobalEvents.emit(Events.AUTH_ERROR, { error: error.message });
      return {
        success: false,
        error: error.message || 'Failed to send password reset email',
      };
    }
  }

  /**
   * Send email verification
   */
  async sendVerificationEmail() {
    if (!this.currentUser) {
      return {
        success: false,
        error: 'No user signed in',
      };
    }

    try {
      await sendEmailVerification(this.currentUser);
      GlobalEvents.emit(Events.VERIFICATION_SENT, { type: 'email' });
      return { success: true };
    } catch (error) {
      GlobalEvents.emit(Events.VERIFICATION_FAILED, {
        type: 'email',
        error: error.message,
      });
      return {
        success: false,
        error: this._getAuthErrorMessage(error),
      };
    }
  }

  /**
   * Update user password
   */
  async updatePassword(newPassword) {
    if (!this.currentUser) {
      return {
        success: false,
        error: 'No user signed in',
      };
    }

    try {
      await updatePassword(this.currentUser, newPassword);
      return { success: true };
    } catch (error) {
      GlobalEvents.emit(Events.AUTH_ERROR, { error: error.message });
      return {
        success: false,
        error: this._getAuthErrorMessage(error),
      };
    }
  }

  /**
   * Get current user
   */
  getCurrentUser() {
    return this.currentUser ? this._sanitizeUser(this.currentUser) : null;
  }

  /**
   * Get ID token
   */
  async getIdToken(forceRefresh = false) {
    if (!this.currentUser) {
      throw new Error('No user signed in');
    }

    return await getIdToken(this.currentUser, forceRefresh);
  }

  /**
   * Get ID token with claims
   */
  async getIdTokenResult(forceRefresh = false) {
    if (!this.currentUser) {
      throw new Error('No user signed in');
    }

    const tokenResult = await getIdTokenResult(this.currentUser, forceRefresh);
    return {
      token: tokenResult.token,
      claims: tokenResult.claims,
      expirationTime: tokenResult.expirationTime,
      issuedAtTime: tokenResult.issuedAtTime,
    };
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return !!this.currentUser;
  }

  /**
   * Check if user email is verified
   */
  isEmailVerified() {
    return this.currentUser?.emailVerified || false;
  }

  /**
   * Sanitize user object for safe transmission
   */
  _sanitizeUser(user) {
    if (!user) return null;

    return {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      emailVerified: user.emailVerified,
      phoneNumber: user.phoneNumber,
      metadata: {
        creationTime: user.metadata.creationTime,
        lastSignInTime: user.metadata.lastSignInTime,
      },
    };
  }

  /**
   * Get user-friendly error message
   */
  _getAuthErrorMessage(error) {
    const errorMessages = {
      'auth/email-already-in-use': 'This email is already registered',
      'auth/invalid-email': 'Invalid email address',
      'auth/operation-not-allowed': 'Operation not allowed',
      'auth/weak-password': 'Password is too weak',
      'auth/user-disabled': 'This account has been disabled',
      'auth/user-not-found': 'No account found with this email',
      'auth/wrong-password': 'Incorrect password',
      'auth/invalid-credential': 'Invalid email or password',
      'auth/too-many-requests': 'Too many failed attempts. Please try again later',
      'auth/network-request-failed': 'Network error. Please check your connection',
      'auth/requires-recent-login': 'Please sign in again to complete this action',
    };

    return errorMessages[error.code] || error.message || 'An error occurred';
  }

  /**
   * Clean up service
   */
  destroy() {
    this._clearTokenRefresh();
    if (this.authStateUnsubscribe) {
      this.authStateUnsubscribe();
    }
    this.initialized = false;
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;
